/**
 * A run's status as a 6px dot plus, optionally, its label.
 *
 * For the run rail, where fifty rows of the word "succeeded" is noise and the two
 * rows that are not succeeded are the whole signal. The run header keeps
 * `StatusBadge`, which spells the value out beside a single run.
 *
 * The mapping comes from `lib/runStatus`, which is also where the reasoning behind
 * each colour lives. Two things this must not do, both of which have shipped in
 * this repo's history: collapse `empty` into `succeeded` or `blocked` into `failed`,
 * and fall back to `succeeded` for a status it does not recognise.
 *
 * The dot is `aria-hidden` and the label carries the accessible name. When the label
 * is hidden the name moves into `sr-only` text rather than a `title`, because colour
 * is the only remaining channel and a screen reader gets nothing from a hue.
 */
import { runStatusLook } from '../../lib/runStatus'
import { cn } from '../../lib/utils'

interface Props {
  status: string | null | undefined
  /** Show the label beside the dot. Off in the rail, on where there is room. */
  showLabel?: boolean
  className?: string
}

export function StatusDot({ status, showLabel = false, className }: Props) {
  const look = runStatusLook(status)
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span
        aria-hidden="true"
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', look.dot)}
      />
      {showLabel ? (
        <span className="truncate text-xs text-slate-400">{look.label}</span>
      ) : (
        <span className="sr-only">{look.label}</span>
      )}
    </span>
  )
}
